import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useAgentWorkflowActionsStore } from "./agentWorkflowActionsStore";
import { useAgentWorkflowTriggersStore } from "./agentWorkflowTriggersStore";
import { useAuthStore } from "./authStore";
import { JSONSchema7 } from "json-schema";
import { Asset } from "./quoteStore";
import { AgentWorkflowDefinition } from "./agentsStore";
import {
  AgentWorkflowRunStatus,
  AgentWorkflowRunTaskStatus,
} from "@forgepoint/shared";

// Operation status enum matching backend
export enum AgentWorkflowRunStepOperationStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  REQUIRES_REVIEW = "REQUIRES_REVIEW",
  WAITING = "WAITING",
  CANCELLED = "CANCELLED",
}

// Operation type enum matching backend
export enum AgentWorkflowRunStepOperationType {
  ASSISTANT_MESSAGE = "ASSISTANT_MESSAGE",
  ASSISTANT_REASONING = "ASSISTANT_REASONING",
  USER_MESSAGE = "USER_MESSAGE",
  TOOL_CALL = "TOOL_CALL",
  CONDITION_CHOICE = "CONDITION_CHOICE",
  SUMMARY = "SUMMARY",
  TRIGGER = "TRIGGER",
}

// Operation parameter types
export interface AssistantMessageOperationParameters {
  type: AgentWorkflowRunStepOperationType.ASSISTANT_MESSAGE;
  actionId: string;
  message: string;
  assetIds: string[] | null;
  assets?: Asset[];
}

export interface AssistantReasoningOperationParameters {
  type: AgentWorkflowRunStepOperationType.ASSISTANT_REASONING;
  reasoning: string;
}

export interface UserMessageOperationParameters {
  type: AgentWorkflowRunStepOperationType.USER_MESSAGE;
  userId: string;
  triggerId: string;
  message: string;
  assetIds: string[] | null;
  userName?: string | null;
  userEmail?: string | null;

  user?: HydratedUser | null;
  trigger?: HydratedTrigger | null;
  assets?: Asset[];
}

export interface ToolCallSentOperationParameters {
  type: AgentWorkflowRunStepOperationType.TOOL_CALL;
  actionId: string;
  callId: string;
  input: unknown;
  error: string | null;
  output: unknown;

  action?: HydratedAction | null;
}

export interface SummaryOperationParameters {
  type: AgentWorkflowRunStepOperationType.SUMMARY;
  summary: string;
}

export interface ConditionChoiceOperationParameters {
  type: AgentWorkflowRunStepOperationType.CONDITION_CHOICE;
  edgeId: string;
  condition: string;
}

export interface TriggerOperationParameters {
  type: AgentWorkflowRunStepOperationType.TRIGGER;
  triggerId: string;
  output: unknown;

  trigger?: HydratedTrigger | null;
}

export type AgentWorkflowRunStepOperationParameters =
  | AssistantMessageOperationParameters
  | AssistantReasoningOperationParameters
  | UserMessageOperationParameters
  | ToolCallSentOperationParameters
  | ConditionChoiceOperationParameters
  | SummaryOperationParameters
  | TriggerOperationParameters;

// Hydrated types for actions, triggers, and users
export interface HydratedAction {
  id: string;
  name: string;
  description: string;
  type: "AGENT" | "ACTION" | "CONDITION";
  inputSchema: JSONSchema7;
  outputSchema: JSONSchema7;
}

export interface HydratedTrigger {
  id: string;
  name: string;
  description: string;
}

export interface HydratedUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

// Operation with hydrated data
export interface WorkflowRunStepOperation {
  id: string;
  agentWorkflowRunStepId: string;
  status: AgentWorkflowRunStepOperationStatus;
  type: AgentWorkflowRunStepOperationType;
  operationParameters: AgentWorkflowRunStepOperationParameters;
  createdAt: string;
  updatedAt: string;
  // Hydrated data

  task?: WorkflowRunTask | null;
}

/**
 * Status enum for workflow run steps (matching backend).
 * This is an alias for AgentWorkflowRunStatus since both use the same statuses.
 */
export type AgentWorkflowRunStepStatus = AgentWorkflowRunStatus;
export const AgentWorkflowRunStepStatus = AgentWorkflowRunStatus;

/**
 * Represents a single step within a workflow run.
 * Each step corresponds to executing a node in the workflow definition.
 */
export interface WorkflowRunStep {
  id: string;
  agentWorkflowRunId: string;
  idempotencyKey: string | null;
  nodeId: string;
  nextEdgeId: string | null;
  inputContext: string | null;
  attempt: number;
  inputParameters: unknown;
  output: unknown;
  status: AgentWorkflowRunStepStatus;
  failureReason?: string | null;
  failedAt?: string | null;
  retryOfStepId?: string | null;
  createdAt: string;
  updatedAt: string;
  operations: WorkflowRunStepOperation[];
}

// Workflow version trigger
export interface WorkflowVersionTrigger {
  id: string;
  agentWorkflowVersionId: string;
  triggerId: string;
  triggerNode: unknown;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

// Workflow version with triggers
export interface WorkflowVersionWithTriggers {
  id: string;
  agentWorkflowId: string;
  version: number;
  definition: AgentWorkflowDefinition | null;
  createdByUserId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  triggers: WorkflowVersionTrigger[];
  allowsChatReplies?: boolean;
  allowsChat?: boolean;
}

export { AgentWorkflowRunTaskStatus };

// Task assignee type
export interface WorkflowRunTaskAssignee {
  id: string;
  assigneeUserId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string;
}

// Task type for pending reviews
export interface WorkflowRunTask {
  id: string;
  status: AgentWorkflowRunTaskStatus;
  agentWorkflowRunStepId: string;
  agentWorkflowRunId: string;
  createdAt: string;
  updatedAt: string;
  assignees?: WorkflowRunTaskAssignee[];
}

// Main workflow run type
export { AgentWorkflowRunStatus };

export interface WorkflowRun {
  id: string;
  name: string | null;
  agentWorkflowId: string;
  agentWorkflowVersionId: string;
  organizationId: string;
  triggerEventId: string | null;
  idempotencyKey: string | null;
  status: AgentWorkflowRunStatus;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  workflowVersion?: WorkflowVersionWithTriggers;
  waitUntil?: string | null;

  steps?: WorkflowRunStep[];
}

// State for each workflow run
export interface WorkflowRunState {
  isLoadingGet: boolean;
  isLoadingCreate: boolean;
  getError: string | null;
  createError: string | null;
  isRetrying: boolean;
  retryError: string | null;
}

// Store interface
interface WorkflowRunStore {
  runMap: Map<string, WorkflowRun>;
  runStateMap: Map<string, WorkflowRunState>;
  workflowRunsMap: Map<string, string[]>; // workflowId -> runIds[]
  workflowRunsByAgent: Map<string, string[]>; // agentId -> runIds[]
  workflowsPendingReview: string[]; // Array of runIds
  pollingIntervals: Map<string, ReturnType<typeof setInterval>>; // runId -> interval
  isLoadingList: boolean;
  isLoadingPendingReview: boolean;
  listError: string | null;
  pendingReviewError: string | null;
  approvingTaskId: string | null;
  denyingTaskId: string | null;

  fetchWorkflowRuns: (
    workflowId: string,
    limit?: number,
    offset?: number,
  ) => Promise<void>;
  fetchPendingReviewRuns: () => Promise<void>;
  getWorkflowRun: (runId: string) => Promise<void>;
  sendAgentChat: (
    workflowId: string,
    workflowRunId: string | null,
    message: string,
    assetIds?: string[],
  ) => Promise<WorkflowRun | null>;
  approveTask: (taskId: string, workflowRunId: string) => Promise<boolean>;
  denyTask: (taskId: string, workflowRunId: string) => Promise<boolean>;
  getRunById: (runId: string) => WorkflowRun | undefined;
  getRunState: (runId: string) => WorkflowRunState;
  getRunsByWorkflowId: (workflowId: string) => WorkflowRun[];
  getRunsByAgentId: (agentId: string) => WorkflowRun[];
  fetchWorkflowRunsByAgent: (
    agentId: string,
    limit?: number,
    offset?: number,
  ) => Promise<void>;
  retryWorkflowRun: (runId: string) => Promise<void>;
  pollUntilRunningOrQueued: (runId: string, intervalMs?: number) => void;
  pollUntilCompletionOrFailed: (runId: string, intervalMs?: number) => void;
  startPolling: (runId: string, intervalMs?: number) => void;
  stopPolling: (runId: string) => void;
  stopAllPolling: () => void;
}

const getDefaultRunState = (): WorkflowRunState => ({
  isLoadingGet: false,
  isLoadingCreate: false,
  getError: null,
  createError: null,
  isRetrying: false,
  retryError: null,
});

/**
 * Merges new workflow runs into the store, preserving existing steps data.
 * Returns updated maps for runMap and runStateMap, plus array of merged run IDs.
 */
const mergeRunsIntoStore = (
  newRuns: WorkflowRun[],
  existingRunMap: Map<string, WorkflowRun>,
  existingRunStateMap: Map<string, WorkflowRunState>,
): {
  runMap: Map<string, WorkflowRun>;
  runStateMap: Map<string, WorkflowRunState>;
  runIds: string[];
} => {
  const runMap = new Map(existingRunMap);
  const runStateMap = new Map(existingRunStateMap);
  const runIds: string[] = [];

  newRuns.forEach((run) => {
    const existingRun = existingRunMap.get(run.id);
    runMap.set(run.id, {
      ...existingRun,
      ...run,
      steps: run.steps || existingRun?.steps,
    });
    runIds.push(run.id);
    if (!existingRunStateMap.has(run.id)) {
      runStateMap.set(run.id, getDefaultRunState());
    }
  });

  return { runMap, runStateMap, runIds };
};

export const useWorkflowRunStore = create<WorkflowRunStore>((set, get) => ({
  runMap: new Map(),
  runStateMap: new Map(),
  workflowRunsMap: new Map(),
  workflowRunsByAgent: new Map(),
  workflowsPendingReview: [],
  pollingIntervals: new Map(),
  isLoadingList: false,
  isLoadingPendingReview: false,
  listError: null,
  pendingReviewError: null,
  approvingTaskId: null,
  denyingTaskId: null,

  fetchWorkflowRuns: async (
    workflowId: string,
    limit: number = 50,
    offset: number = 0,
  ) => {
    set({ isLoadingList: true, listError: null });

    try {
      const response = await fetchApi(
        `agents/workflows/${workflowId}/runs?limit=${limit}&offset=${offset}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = response.data as { runs: WorkflowRun[]; total: number };
        const runs = data.runs;

        set((state) => {
          const { runMap, runStateMap, runIds } = mergeRunsIntoStore(
            runs,
            state.runMap,
            state.runStateMap,
          );

          const newWorkflowRunsMap = new Map(state.workflowRunsMap);
          newWorkflowRunsMap.set(workflowId, runIds);

          return {
            runMap,
            runStateMap,
            workflowRunsMap: newWorkflowRunsMap,
            isLoadingList: false,
          };
        });
        return;
      }

      set({ listError: "Failed to fetch workflow runs", isLoadingList: false });
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch workflow runs", isLoadingList: false });
    }
  },

  getWorkflowRun: async (runId: string) => {
    // Update loading state for this run
    set((state) => {
      const newRunStateMap = new Map(state.runStateMap);
      const currentState = newRunStateMap.get(runId) || getDefaultRunState();
      newRunStateMap.set(runId, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { runStateMap: newRunStateMap };
    });

    try {
      const response = await fetchApi(`agents/runs/${runId}`, "GET");
      if (response.ok && response.data) {
        const workflowRun = response.data as WorkflowRun;

        // Parse out actions and triggers from operations
        const actionsToUpdate = new Map<string, HydratedAction>();
        const triggersToUpdate = new Map<string, HydratedTrigger>();

        if (workflowRun.steps) {
          workflowRun.steps.forEach((step) => {
            if (step.operations) {
              step.operations.forEach((operation) => {
                if (
                  operation.operationParameters.type ===
                    AgentWorkflowRunStepOperationType.TOOL_CALL &&
                  operation.operationParameters.action
                ) {
                  actionsToUpdate.set(
                    operation.operationParameters.action.id,
                    operation.operationParameters.action,
                  );
                }
                if (
                  operation.operationParameters.type ===
                    AgentWorkflowRunStepOperationType.USER_MESSAGE &&
                  operation.operationParameters.trigger
                ) {
                  triggersToUpdate.set(
                    operation.operationParameters.trigger.id,
                    operation.operationParameters.trigger,
                  );
                }
                if (
                  operation.operationParameters.type ===
                    AgentWorkflowRunStepOperationType.TRIGGER &&
                  operation.operationParameters.trigger
                ) {
                  triggersToUpdate.set(
                    operation.operationParameters.trigger.id,
                    operation.operationParameters.trigger,
                  );
                }
              });
            }
          });
        }
        // Update action store with parsed actions
        if (actionsToUpdate.size > 0) {
          const actionStore = useAgentWorkflowActionsStore.getState();
          const updatedActions = new Map(actionStore.actions);
          actionsToUpdate.forEach((hydratedAction, actionId) => {
            const existingAction = updatedActions.get(actionId);
            if (existingAction) {
              // Update existing action with new data
              updatedActions.set(actionId, {
                ...existingAction,
                ...hydratedAction,
              });
            } else {
              // Create minimal action definition if it doesn't exist
              updatedActions.set(actionId, {
                ...hydratedAction,
              });
            }
          });
          useAgentWorkflowActionsStore.setState({ actions: updatedActions });
        }

        // Update trigger store with parsed triggers
        if (triggersToUpdate.size > 0) {
          const triggerStore = useAgentWorkflowTriggersStore.getState();
          const updatedTriggers = new Map(triggerStore.triggers);
          triggersToUpdate.forEach((hydratedTrigger, triggerId) => {
            const existingTrigger = updatedTriggers.get(triggerId);
            if (existingTrigger) {
              // Update existing trigger with new data
              updatedTriggers.set(triggerId, {
                ...existingTrigger,
                name: hydratedTrigger.name,
                description: hydratedTrigger.description,
              });
            } else {
              // Create minimal trigger definition if it doesn't exist
              updatedTriggers.set(triggerId, {
                id: hydratedTrigger.id,
                name: hydratedTrigger.name,
                description: hydratedTrigger.description,
                configurationSchema: {},
                outputSchema: {},
              });
            }
          });
          useAgentWorkflowTriggersStore.setState({ triggers: updatedTriggers });
        }

        set((state) => {
          const newRunMap = new Map(state.runMap);
          newRunMap.set(runId, workflowRun);

          const newRunStateMap = new Map(state.runStateMap);
          const currentState =
            newRunStateMap.get(runId) || getDefaultRunState();
          newRunStateMap.set(runId, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });

          // Update workflowRunsMap if needed
          const newWorkflowRunsMap = new Map(state.workflowRunsMap);
          const existingRunIds =
            newWorkflowRunsMap.get(workflowRun.agentWorkflowId) || [];
          if (!existingRunIds.includes(runId)) {
            newWorkflowRunsMap.set(workflowRun.agentWorkflowId, [
              ...existingRunIds,
              runId,
            ]);
          }

          return {
            runMap: newRunMap,
            runStateMap: newRunStateMap,
            workflowRunsMap: newWorkflowRunsMap,
          };
        });
        return;
      }

      set((state) => {
        const newRunStateMap = new Map(state.runStateMap);
        const currentState = newRunStateMap.get(runId) || getDefaultRunState();
        newRunStateMap.set(runId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch workflow run",
        });
        return { runStateMap: newRunStateMap };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newRunStateMap = new Map(state.runStateMap);
        const currentState = newRunStateMap.get(runId) || getDefaultRunState();
        newRunStateMap.set(runId, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch workflow run",
        });
        return { runStateMap: newRunStateMap };
      });
    }
  },

  sendAgentChat: async (
    workflowId: string,
    workflowRunId: string | null,
    message: string,
    assetIds?: string[],
  ) => {
    try {
      const response = await fetchApi("agents/chat", "POST", {
        body: {
          workflowId,
          workflowRunId,
          message,
          assetIds,
        },
      });

      if (response.ok && response.data) {
        const data = response.data as { workflowRunId: string };
        // After creating, fetch the full run data
        await get().getWorkflowRun(data.workflowRunId);
        return get().runMap.get(data.workflowRunId) || null;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  fetchPendingReviewRuns: async () => {
    set({ isLoadingPendingReview: true, pendingReviewError: null });

    try {
      const userId = useAuthStore.getState().user?.id;
      const params = new URLSearchParams({
        pendingReview: "true",
        limit: "50",
      });
      if (userId) {
        params.set("pendingReviewFromUserId", userId);
      }

      const response = await fetchApi(
        `agents/workflow-runs?${params.toString()}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = response.data as { runs: WorkflowRun[]; total: number };

        set((state) => {
          const { runMap, runStateMap, runIds } = mergeRunsIntoStore(
            data.runs,
            state.runMap,
            state.runStateMap,
          );

          return {
            runMap,
            runStateMap,
            workflowsPendingReview: runIds,
            isLoadingPendingReview: false,
          };
        });
        return;
      }

      set({
        pendingReviewError: "Failed to fetch pending review runs",
        isLoadingPendingReview: false,
      });
    } catch (error) {
      captureException(error as Error);
      set({
        pendingReviewError: "Failed to fetch pending review runs",
        isLoadingPendingReview: false,
      });
    }
  },

  approveTask: async (taskId: string, workflowRunId: string) => {
    set({ approvingTaskId: taskId });

    try {
      const response = await fetchApi(`tasks/${taskId}/approve`, "POST");

      if (response.ok) {
        // Refresh pending review runs
        await get().fetchPendingReviewRuns();
        // Start polling until the worker picks up the run
        get().pollUntilRunningOrQueued(workflowRunId);
        set({ approvingTaskId: null });
        return true;
      }

      set({ approvingTaskId: null });
      return false;
    } catch (error) {
      captureException(error as Error);
      set({ approvingTaskId: null });
      return false;
    }
  },

  denyTask: async (taskId: string, workflowRunId: string) => {
    set({ denyingTaskId: taskId });

    try {
      const response = await fetchApi(`tasks/${taskId}/deny`, "POST");

      if (response.ok) {
        // Refresh pending review runs
        await get().fetchPendingReviewRuns();
        // Start polling until the worker picks up the run
        get().pollUntilRunningOrQueued(workflowRunId);
        set({ denyingTaskId: null });
        return true;
      }

      set({ denyingTaskId: null });
      return false;
    } catch (error) {
      captureException(error as Error);
      set({ denyingTaskId: null });
      return false;
    }
  },

  getRunById: (runId: string) => {
    return get().runMap.get(runId);
  },

  getRunState: (runId: string) => {
    return get().runStateMap.get(runId) || getDefaultRunState();
  },

  getRunsByWorkflowId: (workflowId: string) => {
    const runIds = get().workflowRunsMap.get(workflowId) || [];
    const runMap = get().runMap;
    return runIds
      .map((id) => runMap.get(id))
      .filter((run): run is WorkflowRun => run !== undefined);
  },

  getRunsByAgentId: (agentId: string) => {
    const runIds = get().workflowRunsByAgent.get(agentId) || [];
    const runMap = get().runMap;
    return runIds
      .map((id) => runMap.get(id))
      .filter((run): run is WorkflowRun => run !== undefined);
  },

  fetchWorkflowRunsByAgent: async (
    agentId: string,
    limit: number = 50,
    offset: number = 0,
  ) => {
    set({ isLoadingList: true, listError: null });

    try {
      const response = await fetchApi(
        `agents/workflows/${agentId}/runs?limit=${limit}&offset=${offset}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = response.data as { runs: WorkflowRun[]; total: number };
        const runs = data.runs;

        set((state) => {
          const { runMap, runStateMap, runIds } = mergeRunsIntoStore(
            runs,
            state.runMap,
            state.runStateMap,
          );

          const newWorkflowRunsByAgent = new Map(state.workflowRunsByAgent);
          newWorkflowRunsByAgent.set(agentId, runIds);

          return {
            runMap,
            runStateMap,
            workflowRunsByAgent: newWorkflowRunsByAgent,
            isLoadingList: false,
          };
        });
        return;
      }

      set({ listError: "Failed to fetch workflow runs", isLoadingList: false });
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch workflow runs", isLoadingList: false });
    }
  },

  retryWorkflowRun: async (runId: string) => {
    set((state) => {
      const newRunStateMap = new Map(state.runStateMap);
      const currentState = newRunStateMap.get(runId) || getDefaultRunState();
      newRunStateMap.set(runId, {
        ...currentState,
        isRetrying: true,
        retryError: null,
      });
      return { runStateMap: newRunStateMap };
    });

    try {
      const response = await fetchApi(`agents/runs/${runId}/retry`, "POST");
      if (response.ok) {
        // Start polling to pick up the transition back to running/succeeded
        get().pollUntilRunningOrQueued(runId);
        set((state) => {
          const newRunStateMap = new Map(state.runStateMap);
          const currentState =
            newRunStateMap.get(runId) || getDefaultRunState();
          newRunStateMap.set(runId, {
            ...currentState,
            isRetrying: false,
            retryError: null,
          });
          return { runStateMap: newRunStateMap };
        });
        return;
      }

      set((state) => {
        const newRunStateMap = new Map(state.runStateMap);
        const currentState = newRunStateMap.get(runId) || getDefaultRunState();
        newRunStateMap.set(runId, {
          ...currentState,
          isRetrying: false,
          retryError: "Failed to retry workflow run",
        });
        return { runStateMap: newRunStateMap };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const newRunStateMap = new Map(state.runStateMap);
        const currentState = newRunStateMap.get(runId) || getDefaultRunState();
        newRunStateMap.set(runId, {
          ...currentState,
          isRetrying: false,
          retryError: "Failed to retry workflow run",
        });
        return { runStateMap: newRunStateMap };
      });
    }
  },

  pollUntilRunningOrQueued: (runId: string, intervalMs: number = 1000) => {
    // Stop existing polling for this runId if any
    const existingInterval = get().pollingIntervals.get(runId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Fetch immediately first
    get().getWorkflowRun(runId);

    // Start new polling interval
    const interval = setInterval(async () => {
      // Fetch updated run data
      await get().getWorkflowRun(runId);

      const run = get().runMap.get(runId);

      // Once the run is in QUEUED or RUNNING state, transition to completion polling
      if (run && (run.status === "QUEUED" || run.status === "RUNNING")) {
        get().stopPolling(runId);
        get().pollUntilCompletionOrFailed(runId);
        return;
      }

      // Also stop if run is already in a terminal state
      if (
        run &&
        (run.status === "SUCCEEDED" ||
          run.status === "FAILED" ||
          run.status === "CANCELLED")
      ) {
        get().stopPolling(runId);
        return;
      }
    }, intervalMs);

    // Store the interval
    set((state) => {
      const newPollingIntervals = new Map(state.pollingIntervals);
      newPollingIntervals.set(runId, interval);
      return { pollingIntervals: newPollingIntervals };
    });
  },

  pollUntilCompletionOrFailed: (runId: string, intervalMs: number = 2000) => {
    // Stop existing polling for this runId if any
    const existingInterval = get().pollingIntervals.get(runId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Start new polling interval
    const interval = setInterval(async () => {
      const run = get().runMap.get(runId);

      // Stop polling if run is in a terminal state
      if (
        !run ||
        run.status === "SUCCEEDED" ||
        run.status === "FAILED" ||
        run.status === "CANCELLED"
      ) {
        get().stopPolling(runId);
        return;
      }

      // Fetch updated run data
      await get().getWorkflowRun(runId);
    }, intervalMs);

    // Store the interval
    set((state) => {
      const newPollingIntervals = new Map(state.pollingIntervals);
      newPollingIntervals.set(runId, interval);
      return { pollingIntervals: newPollingIntervals };
    });
  },

  startPolling: (runId: string, intervalMs: number = 2000) => {
    // Alias for pollUntilCompletionOrFailed for backward compatibility
    get().pollUntilCompletionOrFailed(runId, intervalMs);
  },

  stopPolling: (runId: string) => {
    const interval = get().pollingIntervals.get(runId);
    if (interval) {
      clearInterval(interval);
      set((state) => {
        const newPollingIntervals = new Map(state.pollingIntervals);
        newPollingIntervals.delete(runId);
        return { pollingIntervals: newPollingIntervals };
      });
    }
  },

  stopAllPolling: () => {
    const intervals = get().pollingIntervals;
    intervals.forEach((interval) => clearInterval(interval));
    set({ pollingIntervals: new Map() });
  },
}));
